import React from "react";
import { Button, IconButton, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import LinkedInIcon from "@material-ui/icons/LinkedIn";
import TelegramIcon from "@material-ui/icons/Telegram";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";

import { Link } from "react-router-dom";
import { getTranslate } from "../localization";

import "./home.css";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    direction: theme.direction,
    background: "var(--page-bg)",
    [theme.breakpoints.down("sm")]: {
      minHeight: "calc(100vh - 64px)",
    },
  },
  shell: {
    position: "relative",
    zIndex: 3,
    width: "min(100%, 1320px)",
    margin: "0 auto",
    padding: "72px 48px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.15fr) minmax(340px, .85fr)",
    gap: 56,
    alignItems: "center",
    direction: theme.direction,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      gap: 34,
      padding: "58px 34px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "40px 18px 48px",
      gap: 26,
    },
  },
  intro: {
    minWidth: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  kicker: {
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    padding: "8px 14px",
    marginBottom: 22,
    borderRadius: 999,
    color: "var(--accent)",
    backgroundColor: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
    fontSize: 10,
    fontWeight: 850,
    lineHeight: 1.5,
    letterSpacing: theme.direction === "rtl" ? 0 : ".1em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
    boxShadow: "0 8px 24px rgba(22,139,255,.08)",
  },
  title: {
    margin: 0,
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(2.7rem, 5vw, 5rem)",
    lineHeight: 1.06,
    letterSpacing: theme.direction === "rtl" ? 0 : "-.045em",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
    [theme.breakpoints.down("xs")]: {
      fontSize: "clamp(2.2rem, 12vw, 3.3rem)",
      lineHeight: 1.2,
    },
  },
  name: {
    color: "var(--accent)",
    textShadow: "0 8px 36px rgba(22,139,255,.12)",
  },
  headline: {
    maxWidth: 780,
    marginTop: 24,
    color: "var(--text-primary)",
    fontSize: "clamp(1.05rem, 1.8vw, 1.45rem)",
    lineHeight: 1.7,
    fontWeight: 750,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  desc: {
    maxWidth: 800,
    marginTop: 16,
    color: "var(--text-secondary)",
    fontSize: 14,
    lineHeight: 2,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("xs")]: {
      fontSize: 13,
      lineHeight: 1.95,
    },
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 30,
    justifyContent: "flex-start",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
    },
  },
  primaryButton: {
    position: "relative",
    overflow: "hidden",
    minHeight: 50,
    padding: "9px 22px",
    borderRadius: 13,
    color: "#fff",
    fontSize: 11,
    fontWeight: 850,
    background: "linear-gradient(135deg,#087ff5,#2f9fff)",
    boxShadow: "0 14px 34px rgba(22,139,255,.23)",
    transition: "transform .2s ease, box-shadow .2s ease",
    "&::after": {
      content: '\"\"',
      position: "absolute",
      inset: "-40% auto -40% -45%",
      width: "36%",
      transform: "skewX(-22deg)",
      background: "linear-gradient(90deg,transparent,rgba(255,255,255,.36),transparent)",
      transition: "left .45s ease",
    },
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 18px 42px rgba(22,139,255,.31)",
      background: "linear-gradient(135deg,#168bff,#55b3ff)",
    },
    "&:hover::after": { left: "118%" },
  },
  secondaryButton: {
    minHeight: 50,
    padding: "9px 22px",
    borderRadius: 13,
    color: "var(--text-soft)",
    fontSize: 11,
    fontWeight: 850,
    background: "var(--surface-soft)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
    transition:
      "transform .2s ease, color .2s ease, border-color .2s ease, background-color .2s ease",
    "&:hover": {
      color: "var(--text-primary)",
      transform: "translateY(-3px)",
      borderColor: "var(--border-accent)",
      backgroundColor: "var(--accent-soft)",
    },
  },
  buttonInner: {
    position: "relative",
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    direction: theme.direction,
    gap: 10,
    width: "100%",
  },
  directionalIcon: {
    transform: theme.direction === "rtl" ? "scaleX(-1)" : "none",
  },
  social: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 25,
    justifyContent: "flex-start",
  },
  socialButton: {
    width: 41,
    height: 41,
    color: "var(--text-muted)",
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
    boxShadow: "var(--shadow-sm)",
    "&:hover": {
      color: "var(--accent)",
      transform: "translateY(-3px)",
      borderColor: "var(--border-accent)",
      backgroundColor: "var(--accent-soft)",
    },
  },
  proofPanel: {
    position: "relative",
    minWidth: 0,
    padding: 22,
    borderRadius: 26,
    overflow: "hidden",
    background:
      "linear-gradient(145deg,color-mix(in srgb, var(--surface-strong) 96%, transparent),color-mix(in srgb, var(--surface) 96%, transparent))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-lg)",
    backdropFilter: "blur(12px)",
    "&::before": {
      content: '\"\"',
      position: "absolute",
      width: 250,
      height: 250,
      borderRadius: "50%",
      top: -140,
      right: theme.direction === "rtl" ? "auto" : -110,
      left: theme.direction === "rtl" ? -110 : "auto",
      background: "radial-gradient(circle,var(--glow-a),transparent 68%)",
      pointerEvents: "none",
    },
    [theme.breakpoints.down("xs")]: {
      padding: 17,
      borderRadius: 21,
    },
  },
  identityRow: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    marginBottom: 18,
    paddingBottom: 18,
    borderBottom: "1px solid var(--border-soft)",
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
    },
  },
  proofHeaderIcon: {
    width: 54,
    height: 54,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    color: "var(--accent)",
    background: "linear-gradient(145deg,var(--accent-soft-2),var(--accent-soft))",
    border: "1px solid var(--border-accent)",
    boxShadow: "0 12px 28px rgba(22,139,255,.12)",
  },
  proofHeading: {
    minWidth: 0,
    flex: 1,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  proofTitle: {
    color: "var(--text-primary)",
    fontSize: 16,
    fontWeight: 850,
    marginBottom: 5,
  },
  proofRole: {
    color: "var(--accent)",
    fontSize: 10.5,
    fontWeight: 800,
    marginBottom: 5,
  },
  proofLead: {
    color: "var(--text-muted)",
    fontSize: 11.5,
    lineHeight: 1.75,
  },
  proofList: {
    display: "grid",
    gap: 10,
  },
  proofItem: {
    display: "grid",
    gridTemplateColumns: "42px minmax(0,1fr)",
    gap: 12,
    alignItems: "start",
    direction: theme.direction,
    padding: 14,
    borderRadius: 15,
    background: "var(--surface-soft)",
    border: "1px solid var(--border-soft)",
    transition: "transform .2s ease, border-color .2s ease, background-color .2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      borderColor: "var(--border-accent)",
      background: "var(--accent-soft)",
    },
  },
  proofIcon: {
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    color: "var(--accent)",
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  proofText: {
    minWidth: 0,
    color: "var(--text-secondary)",
    fontSize: 11.5,
    lineHeight: 1.78,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
}));

export default function HomeScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  const proofIcons = [
    <MemoryOutlinedIcon fontSize="small" />,
    <SearchOutlinedIcon fontSize="small" />,
    <CodeOutlinedIcon fontSize="small" />,
  ];

  return (
    <section className={classes.root}>
      <div className="ai-background" aria-hidden="true">
        <div className="ai-grid" />
        <div className="ai-glow ai-glow-one" />
        <div className="ai-glow ai-glow-two" />
        <div className="ai-orbit" />
        <span className="ai-node ai-node-1" />
        <span className="ai-node ai-node-2" />
        <span className="ai-node ai-node-3" />
        <span className="ai-node ai-node-4" />
        <span className="ai-node ai-node-5" />
        <span className="ai-node ai-node-6" />
        <span className="ai-line ai-line-1" />
        <span className="ai-line ai-line-2" />
        <span className="ai-line ai-line-3" />
        <span className="ai-line ai-line-4" />
      </div>

      <div className={classes.shell}>
        <div className={classes.intro}>
          <div className={classes.kicker}>{translate.homeKicker}</div>

          <Typography component="h1" className={classes.title}>
            {translate.hi}
            <span className={classes.name}>{translate.name}</span>
            {translate.hi2}
          </Typography>

          <Typography component="h2" className={classes.headline}>
            {translate.homeHeadline}
          </Typography>

          <Typography component="p" className={classes.desc}>
            {translate.desc}
          </Typography>

          <div className={classes.actions}>
            <Button component={Link} to="/portfolio" className={classes.primaryButton}>
              <span className={classes.buttonInner}>
                <span>{translate.viewProjects}</span>
                <ArrowForwardRoundedIcon className={classes.directionalIcon} fontSize="small" />
              </span>
            </Button>

            <Button
              component="a"
              href="/Mehdi_Talebi_Resume.pdf"
              download
              className={classes.secondaryButton}
            >
              <span className={classes.buttonInner}>
                <span>{translate.downloadcv}</span>
                <GetAppOutlinedIcon fontSize="small" />
              </span>
            </Button>
          </div>

          <div className={classes.social}>
            <a
              href="https://www.linkedin.com/in/mehditalebi-/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <IconButton className={classes.socialButton}>
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </a>
            <a
              href="https://t.me/Mehdita"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
            >
              <IconButton className={classes.socialButton}>
                <TelegramIcon fontSize="small" />
              </IconButton>
            </a>
            <a href="mailto:mehditalebi9675@gmail.com" aria-label="Email">
              <IconButton className={classes.socialButton}>
                <EmailOutlinedIcon fontSize="small" />
              </IconButton>
            </a>
          </div>
        </div>

        <aside className={classes.proofPanel} aria-label={translate.homeProofTitle}>
          <div className={classes.identityRow}>
            <div className={classes.proofHeaderIcon} aria-hidden="true">
              <MemoryOutlinedIcon />
            </div>
            <div className={classes.proofHeading}>
              <div className={classes.proofTitle}>{translate.homeProofTitle}</div>
              <div className={classes.proofRole}>{translate.navRole}</div>
              <div className={classes.proofLead}>{translate.homeProofLead}</div>
            </div>
          </div>

          <div className={classes.proofList}>
            {translate.homeSignals.map((item, index) => (
              <div className={classes.proofItem} key={item}>
                <div className={classes.proofIcon}>{proofIcons[index]}</div>
                <div className={classes.proofText}>{item}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
